import {
  Box,
  Button,
  Flex,
  Heading,
  Spacer,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
} from '@chakra-ui/react';
import { faCopy } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { SmartChipGrid } from '../api/use-lab-workbook';

type SmartChipGridViewProps = {
  title?: string;
  grid: SmartChipGrid;
  // Optional banded coloring for readability / pipetting guidance.
  // Example: { orientation: 'column', step: 3 } colors every 3 columns alternately.
  banding?: {
    orientation: 'row' | 'column';
    step?: number; // defaults to 1
    colors?: readonly [string, string]; // [light, dark]
    offset?: number; // starting offset; defaults to 0
  };
};

export function SmartChipGridView({ title, grid, banding }: SmartChipGridViewProps) {
  const { rowLabels, columnLabels, values, caption } = grid;

  const colors: readonly [string, string] = banding?.colors ?? (['#EECDCD', '#789DE5'] as const);
  const step = banding?.step ?? 1;
  const offset = banding?.offset ?? 0;
  const copyResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [copyState, setCopyState] = useState<'idle' | 'success' | 'error'>('idle');

  const copyableText = useMemo(() => {
    if (values.length === 0) {
      return '';
    }
    return values
      .map(row =>
        row
          .map(cell => {
            if (cell == null) {
              return '';
            }
            return String(cell).trim();
          })
          .join('\t'),
      )
      .join('\n')
      .trimEnd();
  }, [values]);

  const scheduleCopyReset = useCallback(() => {
    if (copyResetTimeoutRef.current) {
      clearTimeout(copyResetTimeoutRef.current);
    }
    copyResetTimeoutRef.current = setTimeout(() => {
      setCopyState('idle');
      copyResetTimeoutRef.current = null;
    }, 2000);
  }, []);

  const handleCopyStable = useCallback(() => {
    if (!copyableText) {
      setCopyState('error');
      scheduleCopyReset();
      return;
    }
    const clipboard = typeof navigator === 'undefined' ? null : navigator.clipboard;
    if (!clipboard || typeof clipboard.writeText !== 'function') {
      setCopyState('error');
      scheduleCopyReset();
      return;
    }
    void clipboard
      .writeText(copyableText)
      .then(() => {
        setCopyState('success');
      })
      .catch(() => {
        setCopyState('error');
      })
      .finally(() => {
        scheduleCopyReset();
      });
  }, [copyableText, scheduleCopyReset]);

  useEffect(() => {
    return () => {
      if (copyResetTimeoutRef.current) {
        clearTimeout(copyResetTimeoutRef.current);
      }
    };
  }, []);

  const tooltipLabel = copyState === 'success' ? 'Copied layout' : copyState === 'error' ? 'Copy failed' : '';

  return (
    <VStack align="stretch" spacing={2}>
      <Flex align="center" mb={3} gap={2}>
        {title ? (
          <Heading size="md" mb={0}>
            {title}
          </Heading>
        ) : null}
        <Spacer />
        <Tooltip label={tooltipLabel} placement="top" openDelay={200}>
          <Button
            leftIcon={<FontAwesomeIcon icon={faCopy} color="gray.600" />}
            size="sm"
            variant="ghost"
            colorScheme="gray"
            color="gray.600"
            fontWeight="small"
            onClick={handleCopyStable}
            isDisabled={!copyableText}
          >
            Copy table
          </Button>
        </Tooltip>
      </Flex>
      <Box borderWidth="1px" borderRadius="md" overflow="hidden" bg="white">
        <Box overflowX="auto">
          <Table size="sm" variant="simple" width="max-content" minW="100%">
            <Thead>
              <Tr>
                <Th bg="gray.100" fontWeight="semibold">
                  {/* spacer column for row labels */}
                </Th>
                {columnLabels.map(column => (
                  <Th key={column} bg="gray.100" textAlign="center" fontWeight="semibold">
                    {column}
                  </Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {values.map((row, rowIndex) => {
                const rowLabel = rowLabels[rowIndex] ?? `${rowIndex + 1}`;
                return (
                  <Tr key={rowLabel}>
                    <Th bg="gray.100" scope="row" fontWeight="medium">
                      {rowLabel}
                    </Th>
                    {row.map((cell, cellIndex) => {
                      const bandIndex = (() => {
                        if (!banding) return null;
                        if (banding.orientation === 'column') {
                          return Math.floor((cellIndex + offset) / Math.max(step, 1));
                        }
                        return Math.floor((rowIndex + offset) / Math.max(step, 1));
                      })();
                      const bg = bandIndex != null ? (bandIndex % 2 === 0 ? colors[0] : colors[1]) : undefined;
                      const rawValue = cell == null ? '' : String(cell).trim();
                      const displayValue = rawValue.length > 0 ? rawValue : '\u00A0';
                      const isWaterPlaceholder = rawValue === 'H2O';
                      return (
                        <Td key={`${rowLabel}-${columnLabels[cellIndex] ?? cellIndex}`} textAlign="center" bg={bg}>
                          <Text
                            fontSize="sm"
                            color="gray.900"
                            fontWeight={isWaterPlaceholder ? 'bold' : undefined}
                            fontStyle={isWaterPlaceholder ? 'italic' : undefined}
                          >
                            {displayValue}
                          </Text>
                        </Td>
                      );
                    })}
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      </Box>
      {caption ? (
        <Box>
          <Text fontSize="sm" color="gray.500">
            {caption}
          </Text>
        </Box>
      ) : null}
    </VStack>
  );
}
